import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useSite } from "../lib/SiteContext";
import {
  Activity, Droplets, Cloud, Settings, Database,
  Cpu, Battery, LogOut, ChevronDown, Home, Zap, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", Icon: Activity },
  { to: "/energy", label: "Energy", Icon: Zap },
  { to: "/water", label: "Water", Icon: Droplets },
  { to: "/weather", label: "Weather", Icon: Cloud },
  { to: "/data-sources", label: "Data Sources", Icon: Database },
  { to: "/devices", label: "Devices", Icon: Cpu },
  { to: "/battery", label: "Battery", Icon: Battery },
  { to: "/settings", label: "Settings", Icon: Settings },
];

function SiteSwitcher() {
  const { sites, currentSite, selectSite } = useSite();
  const [open, setOpen] = useState(false);
  if (!currentSite) return null;
  return (
    <div className="relative" data-testid="site-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-border bg-card hover:border-zinc-700 transition-colors text-left"
        data-testid="site-switcher-button"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Home className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{currentSite.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">{currentSite.location_name || "—"}</div>
          </div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-card border border-border rounded-lg overflow-hidden shadow-2xl">
          {sites.map((s) => {
            // Highlight the seeded showcase site so judges/visitors can pick
            // it instantly even after creating their own.
            const isDemo = s.name === "Lierde Home";
            return (
              <button
                key={s.id}
                onClick={() => { selectSite(s.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-2 ${s.id === currentSite.id ? "bg-secondary" : ""}`}
                data-testid={`site-option-${s.id}`}
              >
                <Home className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="truncate flex-1">{s.name}</span>
                {isDemo && (
                  <span
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#F59E0B]/30 text-[#F59E0B] bg-[#F59E0B]/5"
                    data-testid="site-demo-badge"
                  >
                    Demo
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Sidebar body (used both for desktop sticky and mobile drawer) ---------

function SidebarBody({ user, onLogout, onNavClick }) {
  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="p-5 border-b border-border">
        <Link to="/" onClick={onNavClick} className="flex items-center gap-2.5" data-testid="brand-link">
          <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-heading font-bold">
            D
          </div>
          <div>
            <div className="font-heading font-semibold text-base leading-tight">Dreamhouse</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Flow</div>
          </div>
        </Link>
      </div>

      {/* Site switcher */}
      <div className="p-3 border-b border-border">
        <div className="label-tiny mb-2 px-1">Active Site</div>
        <SiteSwitcher />
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="label-tiny mb-2 px-1">Navigate</div>
        <ul className="space-y-0.5">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/"}
                onClick={onNavClick}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors border-l-2 ${
                    isActive
                      ? "bg-secondary text-foreground border-foreground"
                      : "text-muted-foreground border-transparent hover:bg-secondary/50 hover:text-foreground"
                  }`
                }
              >
                <item.Icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User */}
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="w-8 h-8 rounded-full bg-secondary grid place-items-center text-xs font-semibold">
            {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name || "—"}</div>
            <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            data-testid="logout-button"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Top bar (mobile / narrow only) ---------------------------------------

export function MobileTopBar({ onOpen }) {
  return (
    <div className="lg:hidden sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border" data-testid="mobile-topbar">
      <div className="flex items-center justify-between px-4 h-14">
        <Link to="/" className="flex items-center gap-2" data-testid="mobile-brand">
          <div className="w-7 h-7 rounded-md bg-foreground text-background grid place-items-center font-heading font-bold text-sm">D</div>
          <span className="font-heading font-semibold">Dreamhouse Flow</span>
        </Link>
        <button
          onClick={onOpen}
          aria-label="Open navigation menu"
          className="p-2 rounded-md hover:bg-secondary"
          data-testid="mobile-menu-button"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// ---- Main exports ---------------------------------------------------------

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <aside className="hidden lg:flex flex-col w-64 h-screen sticky top-0 border-r border-border bg-background" data-testid="sidebar-desktop">
      <SidebarBody user={user} onLogout={onLogout} />
    </aside>
  );
}

/**
 * Slide-in drawer for narrow viewports. Renders nothing unless `open=true`.
 * Auto-closes on route change so users land on the new page without an
 * overlay still in the way.
 */
export function MobileSidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close the drawer whenever the user navigates to a new route. We track
  // the previous pathname in a ref so the dep array can stay fully exhaustive
  // (no suppression needed) while still firing only on real path changes.
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) return;
    prevPathnameRef.current = location.pathname;
    onClose();
  }, [location.pathname, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const onLogout = async () => {
    onClose();
    await logout();
    navigate("/login");
  };

  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-sidebar">
      {/* Backdrop */}
      <button
        aria-label="Close navigation menu"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        data-testid="mobile-sidebar-backdrop"
      />
      {/* Drawer */}
      <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-background border-r border-border flex flex-col fade-up">
        <div className="absolute top-3 right-3 z-10">
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-md hover:bg-secondary" data-testid="mobile-sidebar-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <SidebarBody user={user} onLogout={onLogout} onNavClick={onClose} />
      </aside>
    </div>
  );
}
